import { Injectable } from '@angular/core';
import { AttachmentDbEntity, FaultDbEntity } from '@docma-mm-core/fault/fault-db.entity';
import { ChangeState } from '@docma-mm-core/model/change-state/change-state';
import { LoadState } from '@docma-mm-core/model/load-state/load-state';
import { SyncState } from '@docma-mm-core/model/sync-state/sync-state';
import { MmDbService } from '../../../mm-db/mm-db-service';
import { MmProjectDbCollections } from '../../../mm-db/mm-project-db-schema';
import { ActivityData } from '../activity';
import {User} from '@docma-mm-core/user/user';

@Injectable()
export class ActivityDbService {
  constructor(private readonly mmDbService: MmDbService) {

  }

  async saveChangeState(mydocmaUid: string, faultUid: string, activityUid: string, changeState: ChangeState<ActivityData>)
                        : Promise<ChangeState<ActivityData>> {
    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    const entity = await db.get(MmProjectDbCollections.faults as never, faultUid);
    if (!entity) {
      throw new Error(`An error while persisting activity change state. Fault uid ${faultUid} not found in project store ${mydocmaUid}`);
    }
    try {
      const activityEntity = entity.activities.find(a => a.uid === activityUid) ;
      if (!activityEntity) {
        throw new Error(`Activity uid ${faultUid}/${activityUid} not found in project store ${mydocmaUid}`);
      }
      activityEntity.changeState = changeState;
    } catch (err) {
      throw new Error(`An error while persisting activity change state in project ${mydocmaUid}.` + err.message);
    }
    db.set(MmProjectDbCollections.faults as never, entity);
    return new ChangeState<ActivityData>(changeState);
  }

  async saveChangeStates(mydocmaUid: string, items: { activityUid: string, faultUid: string }[],
                         changeState: ChangeState<ActivityData>): Promise<ChangeState<ActivityData>> {
    // , faultUids: string[], activityUid: string, changeState: ChangeState<ActivityData>
    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    const entities = await db.getAll(MmProjectDbCollections.faults as never, IDBKeyRange.bind(items.map(x => x.faultUid)));
    if (entities) {
      try {
        const updatedEntities: any[] = [];

        for (const item of items) {
          const entity = entities.find(x => x.uid === item.faultUid);
          if (entity) {
            const activityEntity = entity.activities.find(a => a.uid === item.activityUid);
            if (!activityEntity) {
              throw new Error(`Activity uid ${item.activityUid} not found in project store ${mydocmaUid}`);
            }
            activityEntity.changeState = changeState;
            updatedEntities.push(entity);
          }
        }
        db.putAll(MmProjectDbCollections.faults as never, updatedEntities);
      } catch (err) {
        throw new Error(`An error while persisting activity change state in project ${mydocmaUid}.` + err.message);
      }
    }
    return new ChangeState<ActivityData>(changeState);
  }

  async saveLoadStateModified(mydocmaUid: string, faultUid: string, activityUid: string, modifiedOn: Date, modifier: User,
                      throwErrorIfNotFound?: boolean): Promise<LoadState<ActivityData>> {
    let loadState: any;
    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    const entity = await db.get(MmProjectDbCollections.faults as never, faultUid);
    if (!entity)  {
      if (throwErrorIfNotFound) {
        throw new Error(`An error while persisting activity load state. Fault uid ${faultUid} not found in project store ${mydocmaUid}`);
      } else {
        console.log(`An error while persisting activity load state. Fault uid ${faultUid} not found in project store ${mydocmaUid}`);
      }
      return null;
    }

    try {
      const activityEntity = entity.activities.find(a => a.uid === activityUid) ;
      if (!activityEntity) {
        throw new Error(`Attachment uid ${faultUid}/${activityUid} not found in project store ${mydocmaUid}`);
      }
      activityEntity.loadState.data.ModifiedOn = modifiedOn;
      activityEntity.loadState.data.Modifier = modifier;
      loadState = activityEntity.loadState;
      // activityEntity.id = loadState.data?.Id ?? activityEntity.id;
    } catch (err) {
      if (throwErrorIfNotFound) {
        throw new Error(`An error while persisting activity load state in project ${mydocmaUid}.` + err.message);
      } else {
        console.log(`An error while persisting activity load state in project ${mydocmaUid}.` + err.message);
      }
      return null;
    }
    db.set(MmProjectDbCollections.faults as never, entity);
    return new LoadState<ActivityData>(loadState);
  }

  async saveLoadState(mydocmaUid: string, faultUid: string, activityUid: string, loadState: LoadState<ActivityData>,
                      throwErrorIfNotFound?: boolean): Promise<LoadState<ActivityData>> {
    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    const entity = await db.get(MmProjectDbCollections.faults as never, faultUid);
    if (!entity)  {
      if (throwErrorIfNotFound) {
        throw new Error(`An error while persisting activity load state. Fault uid ${faultUid} not found in project store ${mydocmaUid}`);
      } else {
        console.log(`An error while persisting activity load state. Fault uid ${faultUid} not found in project store ${mydocmaUid}`);
      }
      return null;
    }

    try {
      const activityEntity = entity.activities.find(a => a.uid === activityUid) ;
      if (!activityEntity) {
        throw new Error(`Attachment uid ${faultUid}/${activityUid} not found in project store ${mydocmaUid}`);
      }
      activityEntity.loadState = loadState;
      activityEntity.id = loadState.data?.Id ?? activityEntity.id;
    } catch (err) {
      if (throwErrorIfNotFound) {
        throw new Error(`An error while persisting activity load state in project ${mydocmaUid}.` + err.message);
      } else {
        console.log(`An error while persisting activity load state in project ${mydocmaUid}.` + err.message);
      }
      return null;
    }
    db.set(MmProjectDbCollections.faults as never, entity);
    return new LoadState<ActivityData>(loadState);
  }

  async saveLoadStates(mydocmaUid: string, items: { activityUid: string, faultUid: string, loadState: LoadState<ActivityData> }[],
                       throwErrorIfNotFound?: boolean): Promise<LoadState<ActivityData>[]> {
    const loadStates: LoadState<ActivityData>[] = [];
    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    const entities = await db.getAll(MmProjectDbCollections.faults as never, IDBKeyRange.bind(items.map(m => m.faultUid)));
    if (entities) {
      try {
        const updatedEntities: any[] = [];

        for (const item of items) {
          const entity = entities.find(x => x.uid === item.faultUid);
          const activityEntity = entity?.activities?.find(a => a.uid === item.activityUid);

          if (activityEntity) {
            activityEntity.loadState = item.loadState;
            activityEntity.id = item.loadState.data?.Id ?? activityEntity.id;

            loadStates.push(new LoadState<ActivityData>(item.loadState));
          }
        }
        await db.putAll(MmProjectDbCollections.faults as never, entities);
      } catch (err) {
        if (throwErrorIfNotFound) {
          throw new Error(`An error while persisting activity load state in project ${mydocmaUid}.` + err.message);
        } else {
          console.log(`An error while persisting activity load state in project ${mydocmaUid}.` + err.message);
        }
        return null;
      }
      return loadStates;
    }
  }

  async saveSyncState(mydocmaUid: string, faultUid: string, activityUid: string,
                      state: SyncState, throwErrorIfNotFound?: boolean) {
    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    const entity = await db.get(MmProjectDbCollections.faults as never, faultUid);
    if (entity) {
      try {
        const activityEntity = entity?.activities?.find(a => a.uid === activityUid) ;
        if (activityEntity) {
          activityEntity.syncState = {...state};
          delete activityEntity.syncState.changes;
          await db.set(MmProjectDbCollections.faults as never, entity);
        }
      } catch (err) {
        // TODO: Log error
        if (throwErrorIfNotFound) {
         throw new Error(`An error while persisting activity sync state in project ${mydocmaUid}.` + err.message);
        } else {
          console.log(`An error while persisting activity sync state in project ${mydocmaUid}.` + err.message);
        }
      }
    }
    return state;
  }
  async saveSyncStates(mydocmaUid: string, items: { activityUid: string, faultUid: string }[],
                       state: SyncState, throwErrorIfNotFound?: boolean) {
    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    const entities = await db.getAll(MmProjectDbCollections.faults as never, IDBKeyRange.bind(items.map(m => m.faultUid)));
    if (entities) {
      try {
        const updatedEntities: any[] = [];

        for (const item of items) {
          const entity = entities.find(x => x.uid === item.faultUid);
          const activityEntity = entity?.activities?.find(a => a.uid === item.activityUid) ;
          if (activityEntity) {
            activityEntity.syncState = {...state};
            delete activityEntity.syncState.changes;
            updatedEntities.push(entity);
          }
        }
        await db.putAll(MmProjectDbCollections.faults as never, updatedEntities);
      } catch (err) {
        // TODO: Log error
        if (throwErrorIfNotFound) {
          throw new Error(`An error while persisting activity sync state in project ${mydocmaUid}.` + err.message);
        } else {
          console.log(`An error while persisting activity sync state in project ${mydocmaUid}.` + err.message);
        }
      }
    }
    return state;
  }

}
